import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import type { DebouncedFunc } from 'lodash';
import type { UseQueryResult } from '@tanstack/react-query';
import type { VcsApi } from '@env0/vcs-service/api';
import { useGetVcsRepositories } from 'stores/rq/vcs-connections';

const REPOSITORIES_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 600;

const firstPageOptions = (search: string) => ({
  search: search || undefined,
  page: 1,
  perPage: REPOSITORIES_PAGE_SIZE
});

// The page the dropdown shows before the user types. Same query key as the dropdown's own initial load, so a
// second consumer (the connection picker) reads the cached result instead of fetching again.
export const useVcsRepositoriesFirstPage = (vcsType: VcsApi.VcsType, connectionType: VcsApi.ConnectionType) =>
  useGetVcsRepositories(vcsType, connectionType, firstPageOptions(''));

// Finds one repository the first page does not hold (picked through search, or pinned on an existing template)
// by running the same server-side search with its URL; the backend normalizes a URL to the `org/repo` term.
export const useVcsRepositoryLookup = (
  vcsType: VcsApi.VcsType,
  connectionType: VcsApi.ConnectionType,
  repositoryUrl: string | undefined,
  enabled: boolean
) =>
  useGetVcsRepositories(
    vcsType,
    connectionType,
    firstPageOptions(repositoryUrl ?? ''),
    enabled && Boolean(repositoryUrl)
  );

// Drives the repository dropdowns: the first page loads on open and typing triggers a debounced
// server-side search, so a connection with tens of thousands of repos no longer enumerates them all.
export const useVcsRepositoriesSearch = (
  vcsType: VcsApi.VcsType,
  connectionType: VcsApi.ConnectionType
): UseQueryResult<VcsApi.GetVcsRepositories.Response> & { onSearch: DebouncedFunc<(searchTerm: string) => void> } => {
  const [search, setSearch] = useState('');

  const query = useGetVcsRepositories(vcsType, connectionType, firstPageOptions(search));

  const onSearch = useMemo(() => _.debounce(setSearch, SEARCH_DEBOUNCE_MS), []);
  useEffect(() => () => onSearch.cancel(), [onSearch]);

  return { ...query, onSearch };
};
